import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORD_COLORS = [
  { value: "#2d5424", label: "Sage Green" },
  { value: "#d4788e", label: "Blush" },
  { value: "#b78228", label: "Gold" },
  { value: "#2a2030", label: "Ink" },
  { value: "#fffaf0", label: "Cream" },
];

const SOURCES = [
  { key: "photos", label: "📷 Photos" },
  { key: "illustrations", label: "🎨 Illustrations" },
  { key: "vectors", label: "✦ Clip art / Vectors" },
];

function VisionItem({ item, onChange, onDelete, boardRef }) {
  const dragState = useRef(null);
  const resizeState = useRef(null);

  const onMouseDown = useCallback((e) => {
    if (e.target.closest('.vb-item-resize') || e.target.closest('.vb-item-delete')) return;
    e.preventDefault();
    const board = boardRef.current.getBoundingClientRect();
    dragState.current = {
      offsetX: e.clientX - board.left - item.x,
      offsetY: e.clientY - board.top - item.y,
    };
    const onMove = (ev) => {
      if (!dragState.current) return;
      const b = boardRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(b.width - item.w, ev.clientX - b.left - dragState.current.offsetX));
      const y = Math.max(0, Math.min(b.height - item.h, ev.clientY - b.top - dragState.current.offsetY));
      onChange({ ...item, x, y });
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [item, onChange, boardRef]);

  const onResizeDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeState.current = { startX: e.clientX, startY: e.clientY, w: item.w, h: item.h };
    const onMove = (ev) => {
      if (!resizeState.current) return;
      const dx = ev.clientX - resizeState.current.startX;
      const dy = ev.clientY - resizeState.current.startY;
      const w = Math.max(40, resizeState.current.w + dx);
      const h = Math.max(30, resizeState.current.h + dy);
      onChange({ ...item, w, h });
    };
    const onUp = () => {
      resizeState.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [item, onChange]);

  const style = {
    left: `${item.x}px`, top: `${item.y}px`,
    width: `${item.w}px`, height: `${item.h}px`,
    color: item.color || undefined,
    fontSize: item.kind === "text" ? `${Math.max(14, Math.min(40, item.h * 0.5))}px` : undefined,
  };

  return (
    <div
      className={`vb-item ${item.kind === "text" ? "is-text" : ""}`}
      style={style}
      onMouseDown={onMouseDown}
      data-testid="vb-item"
    >
      {item.kind === "image"
        ? <img src={item.url} alt="" draggable={false} />
        : <span>{item.text}</span>}
      <button className="vb-item-delete" onClick={() => onDelete(item.id)} data-testid="vb-item-delete">×</button>
      <div className="vb-item-resize" onMouseDown={onResizeDown} />
    </div>
  );
}

export default function VisionBoard() {
  const [source, setSource] = useState("photos");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searchState, setSearchState] = useState("idle"); // idle | loading | error
  const [searchErr, setSearchErr] = useState("");
  const [items, setItems] = useState([]);
  const [wordText, setWordText] = useState("");
  const [wordColor, setWordColor] = useState("#2d5424");
  const boardRef = useRef(null);

  // Persist board state in localStorage so users don't lose their work
  const STORAGE_KEY = "fl_bridge_board";
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItems(JSON.parse(saved));
    } catch (_e) { /* ignore */ }
  }, []);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (_e) { /* ignore */ }
  }, [items]);

  const search = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!query.trim()) return;
    setSearchState("loading");
    setSearchErr("");
    try {
      const res = await axios.get(`${API}/bridge/images/search`, {
        params: { q: query.trim(), source, page: 1, per_page: 24 },
      });
      setResults(res.data?.results || []);
      setSearchState("idle");
    } catch (err) {
      setSearchState("error");
      setSearchErr(err?.response?.data?.detail || "Search failed. Please try again.");
    }
  }, [query, source]);

  const addImage = (img) => {
    const board = boardRef.current?.getBoundingClientRect();
    const bw = board?.width || 800;
    const bh = board?.height || 600;
    const w = 160; const h = 160;
    const item = {
      id: `i_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      kind: "image",
      url: img.full,
      x: Math.max(0, Math.random() * (bw - w)),
      y: Math.max(0, Math.random() * (bh - h)),
      w, h,
    };
    setItems((arr) => [...arr, item]);
  };

  const addWord = () => {
    if (!wordText.trim()) return;
    const board = boardRef.current?.getBoundingClientRect();
    const bw = board?.width || 800;
    const bh = board?.height || 600;
    const w = Math.max(120, wordText.length * 14);
    const h = 50;
    const item = {
      id: `t_${Date.now()}`,
      kind: "text",
      text: wordText.trim(),
      color: wordColor,
      x: Math.max(0, Math.random() * (bw - w)),
      y: Math.max(0, Math.random() * (bh - h)),
      w, h,
    };
    setItems((arr) => [...arr, item]);
    setWordText("");
  };

  const updateItem = (next) => setItems((arr) => arr.map((it) => (it.id === next.id ? next : it)));
  const deleteItem = (id) => setItems((arr) => arr.filter((it) => it.id !== id));
  const clearBoard = () => {
    if (window.confirm("Clear the entire vision board?")) setItems([]);
  };
  const printBoard = () => window.print();

  return (
    <div className="bridge-panel" data-testid="bridge-panel-board">
      <h2 className="bridge-panel-heading">Your <em>Vision Board</em></h2>
      <p className="bridge-panel-sub">Search for images that resonate with your dream life. Click them to add to your board, drag to reposition, drag the corner to resize. Print or save when ready.</p>

      <div className="vb-source-toggle" role="tablist">
        {SOURCES.map((s) => (
          <button key={s.key}
            className={`vb-src-btn ${source === s.key ? "active" : ""}`}
            onClick={() => setSource(s.key)}
            data-testid={`vb-source-${s.key}`}>{s.label}</button>
        ))}
      </div>

      <form className="vb-search-row" onSubmit={search}>
        <input type="text" className="bridge-input"
          placeholder="Search: abundance, ocean, family, home, travel…"
          value={query} onChange={(e) => setQuery(e.target.value)}
          data-testid="vb-search-input" />
        <button type="submit" className="bridge-btn" disabled={searchState === "loading"} data-testid="vb-search-btn">
          {searchState === "loading" ? "Searching…" : "Search"}
        </button>
      </form>

      <div className="vb-image-grid" data-testid="vb-image-grid">
        {searchState === "error" && <p style={{ color: "#b91c1c" }}>{searchErr}</p>}
        {searchState === "idle" && results.length === 0 && (
          <p>Search for images above to begin building your vision board ✦</p>
        )}
        {results.map((img) => (
          <div key={img.id} className="vb-image-tile" onClick={() => addImage(img)} data-testid="vb-image-tile">
            <img src={img.thumb} alt="" loading="lazy" />
          </div>
        ))}
      </div>

      <p className="vb-instructions">✦ Click an image to add it to your board · Drag to reposition · Drag the corner to resize · Hover to delete</p>

      <div className="vb-word-row">
        <input type="text" className="bridge-input"
          placeholder="Add a word or affirmation to your board…"
          value={wordText} onChange={(e) => setWordText(e.target.value)}
          maxLength={60}
          data-testid="vb-word-input" />
        <select value={wordColor} onChange={(e) => setWordColor(e.target.value)} data-testid="vb-word-color">
          {WORD_COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <button className="bridge-btn bridge-btn-ghost" onClick={addWord} type="button" data-testid="vb-add-word">Add Word</button>
      </div>

      <div id="vb-board" ref={boardRef} data-testid="vb-board">
        {items.length === 0 && (
          <div className="vb-board-placeholder">
            <span style={{ fontSize: 40 }}>✦</span>
            <p style={{ margin: 0, fontWeight: 700, color: "var(--bridge-sage)" }}>Your vision board begins here</p>
            <p style={{ margin: 0, fontSize: 12 }}>Search and click images above to add them</p>
          </div>
        )}
        {items.map((it) => (
          <VisionItem key={it.id} item={it} onChange={updateItem} onDelete={deleteItem} boardRef={boardRef} />
        ))}
      </div>

      <div className="vb-controls">
        <button className="bridge-btn bridge-btn-ghost" onClick={clearBoard} type="button" data-testid="vb-clear">Clear Board</button>
        <button className="bridge-btn bridge-btn-gold" onClick={printBoard} type="button" data-testid="vb-print">🖨 Print / Save Board</button>
      </div>
    </div>
  );
}
